import React, { FunctionComponent, useState } from "react";
import { Box, DialogContent, Stack, Toolbar } from "@mui/material";
import { strings } from "../../../localization/LocalizedStrings";
import { ISubscription } from "../../../hooks/subscriptions";
import { AlertDialog } from "../../AlertDialog";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import styles from "./styles.module.scss";
import { RoutesBuilder } from "../../../models/RoutesBuilder";
import { useNavigate } from "react-router";
import { useSessionManager, useUser } from "../../../hooks/session";
import { PurchaseOptionCard } from "../PurchaseOptionCard";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { useGetPatreonUserId } from "../../../hooks/patreon";
import { useOpenWindow } from "../../../hooks/openWindow";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons/IconType";

export const SubscriptionDialog: FunctionComponent<ISubscriptionDialogProps> = ({
  subscription,
  isOpen,
  setIsOpen
}) => {
  const navigate = useNavigate();
  const getPatreonUserId = useGetPatreonUserId();
  const { closeSession } = useSessionManager();
  const { openInNewTab, openInSameTab } = useOpenWindow();
  const [isPayWithMercadoPagoAlertOpen, setIsPayWithMercadoPagoAlertOpen] = useState(false);
  const [isSuccessAlertOpen, setIsSuccessAlertOpen] = useState(false);
  const [currentUser] = useUser.useState();
  return (
    <PiperDialog
      fullWidth
      maxWidth={"xs"}
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
    >
      <Toolbar>
        <h3>{subscription.name}</h3>
        <Box flexGrow={1} />
        <ToolbarButton
          onClick={() => {
            setIsOpen(false);
          }}
          tooltip={strings.close}
          iconType={IconType.Close}
        />
      </Toolbar>
      <DialogContent>
        <Stack className={styles.textStack} spacing={2}>
          <p className={styles.description}>{subscription.description}</p>
          <PurchaseOptionCard
            isSubscription
            price={subscription.finalPriceARS}
            currency={"ARS"}
            imageSrc={"https://piper-public-images.s3.us-east-2.amazonaws.com/MercadoPagoLogo.jpg"}
            buttonText={strings.subscription_pay_with_mercadopago}
            description={strings.subscription_pay_info}
            optionText={strings.pay_option_mercado_pago}
            onClick={() => setIsPayWithMercadoPagoAlertOpen(true)}
          />
          <PurchaseOptionCard
            isSubscription
            price={subscription.finalPriceUSD}
            currency={"USD"}
            imageSrc={"https://piper-public-images.s3.us-east-2.amazonaws.com/PatreonLogo.png"}
            buttonText={strings.subscription_pay_with_patreon}
            description={strings.subscription_pay_info_patreon}
            optionText={strings.pay_option_patreon}
            onClick={async () => {
              const { status } = await getPatreonUserId();
              if (StatusCodesHelper.isSuccessful(status)) {
                openInSameTab(
                  "https://patreon.com/Piper107?utm_medium=piper&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink"
                );
              } else {
                openInSameTab(
                  `https://www.patreon.com/oauth2/authorize?response_type=code&client_id=${process.env.PATREON_CLIENT_ID}&redirect_uri=${process.env.URL}/paymentPatreon&state=${currentUser.uuid}`
                );
              }
            }}
          />
          <PurchaseOptionCard
            isSubscription
            price={subscription.finalPriceUSD}
            currency={"USD"}
            imageSrc={"https://piper-public-images.s3.us-east-2.amazonaws.com/AppleLogo.svg"}
            buttonText={strings.subscription_pay_with_apple}
            description={strings.subscription_pay_info_apple}
            optionText={strings.pay_option_apple}
            onClick={() => openInNewTab(`https://apple.co/3P2tpt7`)}
          />
        </Stack>
      </DialogContent>
      <AlertDialog
        message={strings.subscription_pay_with_mercadopago_alert}
        open={isPayWithMercadoPagoAlertOpen}
        setOpen={setIsPayWithMercadoPagoAlertOpen}
        isConfirm
        onConfirm={() =>
          openInSameTab(
            `https://www.mercadopago.com.ar/subscriptions/checkout?preapproval_plan_id=${subscription.mercadoPagoPlanId}`
          )
        }
      />
      <AlertDialog
        title={strings.app_name}
        message={strings.subscription_success}
        open={isSuccessAlertOpen}
        setOpen={setIsSuccessAlertOpen}
        onConfirm={() => {
          closeSession();
          navigate(RoutesBuilder.home());
        }}
      />
    </PiperDialog>
  );
};

export interface ISubscriptionDialogProps {
  subscription: ISubscription;
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
}
